import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import clsx from 'clsx';
import { 
  BookOpen, 
  LogOut, 
  Settings, 
  Mic, 
  Sparkles,
  Target,
  Users,
  TrendingUp,
  Brain,
  Crown,
  ChevronRight,
  Award,
  X,
  Heart,
  Zap,
  Volume2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Bell,
  Users2,
  ArrowRight,
  HelpCircle
} from 'lucide-react';
import { PremiumButton } from '../components/premium/PremiumButton';
import IntakeModal from '../components/premium/IntakeModal';
import { AccountTypeSelectionModal } from '../components/premium/AccountTypeSelectionModal';
import { IndividualSubscriptionModal } from '../components/premium/IndividualSubscriptionModal';
// 🧭 Aide & Navigation — premium help modals (desktop + mobile)
import HelpDesktopModal from '../components/help/HelpDesktopModal';
import HelpMobileSheet from '../components/help/HelpMobileSheet';
// useModuleAudioAutomation SUPPRIMÉ — audio = lecture seule côté utilisateur (P1 Séparation admin/user)
// ProactiveNotifications SUPPRIMÉ — P1.2 NotificationBadge est le seul point d'entrée UX (Décision MOA)

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Premium Light Design System
const premiumTheme = {
  gold: {
    gradient: 'linear-gradient(135deg, #FFD86F 0%, #FFB347 100%)',
    text: 'linear-gradient(to right, #FFD86F 0%, #FFF5C3 100%)',
    glow: 'rgba(255, 216, 111, 0.3)'
  },
  purple: {
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)',
    dark: '#1C104A'
  }
};

// Pillar configuration with light gradients
const pillarConfig = [
  {
    icon: Brain,
    gradient: 'from-cyan-200 via-blue-100 to-violet-100',
    glow: 'rgba(139, 92, 246, 0.2)',
    accentColor: '#06b6d4'
  },
  {
    icon: Users,
    gradient: 'from-blue-200 via-indigo-100 to-purple-100',
    glow: 'rgba(99, 102, 241, 0.2)',
    accentColor: '#6366f1'
  },
  {
    icon: TrendingUp,
    gradient: 'from-violet-200 via-purple-100 to-fuchsia-100',
    glow: 'rgba(168, 85, 247, 0.2)',
    accentColor: '#a855f7'
  },
  {
    icon: Target,
    gradient: 'from-fuchsia-200 via-pink-100 to-rose-100',
    glow: 'rgba(236, 72, 153, 0.2)',
    accentColor: '#ec4899'
  },
  {
    icon: Award,
    gradient: 'from-amber-200 via-yellow-100 to-orange-100',
    glow: 'rgba(251, 191, 36, 0.2)',
    accentColor: '#fbbf24'
  }
];

// Recommendation type configs
const recommendationTypeConfig = {
  emotion_based: {
    icon: Heart,
    gradient: 'from-rose-200 to-pink-100',
    accentColor: '#f43f5e',
    label: 'Émotionnel'
  },
  progression_based: {
    icon: TrendingUp,
    gradient: 'from-violet-200 to-purple-100',
    accentColor: '#a855f7',
    label: 'Progression'
  },
  deblocage: {
    icon: Zap,
    gradient: 'from-amber-200 to-yellow-100',
    accentColor: '#f59e0b',
    label: 'Déblocage'
  }
};

// 🎯 P1.2: Badge Notifications Actions suggérées Owen
const P12NotificationBadge = ({ token }) => {
  const [pendingCount, setPendingCount] = useState(0);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!token) return;
    
    let isMounted = true;
    
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API}/owen/feedbacks/count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok && isMounted) {
          const data = await res.json();
          setPendingCount(data.pending || 0);
        }
      } catch (err) {
        console.error('Erreur chargement notifications P1.2:', err);
      }
    };
    
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [token]);
  
  return (
    <button
      onClick={() => navigate('/notifications')}
      className="relative p-2.5 rounded-xl bg-white/80 backdrop-blur-sm shadow-md hover:shadow-lg hover:bg-amber-50 transition-all duration-200 border border-amber-100"
      data-testid="p12-notification-badge"
      aria-label={`Actions suggérées${pendingCount > 0 ? ` (${pendingCount} en attente)` : ''}`}
    >
      <Bell className="w-5 h-5 text-amber-600" />
      {pendingCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1.5 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse shadow-sm">
          {pendingCount > 9 ? '9+' : pendingCount}
        </span>
      )}
    </button>
  );
};

const TrainingDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, token } = useAuth();
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalProgress, setGlobalProgress] = useState(0);
  const [pillarsProgress, setPillarsProgress] = useState({});
  
  // Owen 360° State
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  
  // Pathway preview state
  const [pathwayPreview, setPathwayPreview] = useState(null);
  const [loadingPathway, setLoadingPathway] = useState(false);
  // 🧭 Aide & Navigation
  const [helpOpen, setHelpOpen] = useState(false);
  
  // 🎯 P1 MOA: États pour la modale PREMIUM (TOUJOURS accessible)
  const [showTypeSelectionModal, setShowTypeSelectionModal] = useState(false);
  const [showPackModal, setShowPackModal] = useState(false);

  // ✨ Point d'entrée COACHDIA COACHDIA Prism — modale d'orientation (réutilise /discovery et /team-insights)
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  
  // 🎯 P1 MOA: Handlers pour sélection type de compte
  const handlePremiumClick = () => {
    setShowTypeSelectionModal(true);
  };
  
  const handleSelectIndividual = () => {
    setShowTypeSelectionModal(false);
    setShowPackModal(true);
  };
  
  const handleSelectEnterprise = () => {
    setShowTypeSelectionModal(false);
    navigate('/subscription/enterprise');
  };
  
  // 🔒 P0 FIX: Ouvrir IntakeModal si paramètre URL openIntake=true
  useEffect(() => {
    if (searchParams.get('openIntake') === 'true') {
      setShowIntakeModal(true);
      // Nettoyer le paramètre URL après ouverture
      searchParams.delete('openIntake');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  useEffect(() => {
    fetchPillars();
    if (user) {
      fetchGlobalProgress();
      fetchPillarsProgress();
      fetchRecommendations();
      fetchPathwayPreview();
    }
  }, [user]);

  const fetchPillars = async () => {
    try {
      const response = await axios.get(`${API}/pillars`);
      setPillars(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des piliers');
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalProgress = async () => {
    try {
      const response = await axios.get(`${API}/progress/global`);
      setGlobalProgress(response.data.progress_percentage || 0);
    } catch (error) {
      console.error('Erreur lors du chargement de la progression:', error);
    }
  };

  const fetchPillarsProgress = async () => {
    try {
      const response = await axios.get(`${API}/progress/pillars`);
      // Convert array to object for easier lookup by pillar_id
      const progressMap = {};
      response.data.forEach(item => {
        progressMap[item.pillar_id] = item.progress_percentage;
      });
      setPillarsProgress(progressMap);
    } catch (error) {
      console.error('Erreur lors du chargement de la progression des piliers:', error);
    }
  };

  const fetchRecommendations = async () => {
    if (!user?.user_id) return;
    
    try {
      setLoadingRecommendations(true);
      
      // Fetch real recommendations from backend
      const response = await axios.get(
        `${API}/ai/recommendations/${user.user_id}?max_recommendations=3&include_analysis=false`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          timeout: 5000
        }
      );
      
      if (response.data.recommendations && response.data.recommendations.length > 0) {
        setRecommendations(response.data.recommendations);
        console.log('✅ Owen 360° Recommendations loaded:', response.data.recommendations.length);
      } else {
        // No recommendations available
        setRecommendations([]);
        console.log('ℹ️ No recommendations available yet');
      }
    } catch (error) {
      console.error('⚠️ Erreur lors du chargement des recommandations:', error);
      setRecommendations([]);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const handleExploreModule = (moduleId) => {
    console.log('📍 Navigating to module:', moduleId);
    
    // Vérification que moduleId existe
    if (!moduleId) {
      console.error('❌ Module ID is missing');
      toast.error('Impossible d\'ouvrir ce module. ID manquant.');
      return;
    }
    
    // Vérifier le format du moduleId
    console.log('Module ID type:', typeof moduleId);
    console.log('Module ID value:', moduleId);
    
    try {
      // Utiliser module-v2 qui utilise l'API modules-v2 (plus fiable)
      navigate(`/module-v2/${moduleId}`);
    } catch (error) {
      console.error('❌ Navigation error:', error);
      toast.error('Erreur lors de la navigation vers le module.');
    }
  };
  
  const handleIntakeComplete = () => {
    console.log('✅ Intake completed, redirecting to pathway...');
    setShowIntakeModal(false);
    toast.success('🎯 Ton parcours personnalisé est prêt !', {
      duration: 3000
    });
    // Redirect to Owen pathway page
    setTimeout(() => {
      navigate('/my-owen-path');
    }, 1500);
  };
  
  const fetchPathwayPreview = async () => {
    if (!user?.email) return;
    try {
      setLoadingPathway(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/owen/pathway/${encodeURIComponent(user.email)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setPathwayPreview(response.data);
    } catch (error) {
      // 🟡 P1 (Fev 2026) — Le 404 "No active pathway found" est un cas
      // métier légitime (intake non encore complété), pas une erreur.
      // On le tait pour ne pas polluer la console. `MyOwenPath.js` gère
      // déjà l'UX dédiée (« Aucun parcours trouvé » + CTA intake) — ce
      // composant ne montre simplement pas le preview, comportement OK.
      const status = error?.response?.status;
      if (status !== 404) {
        console.warn('[TrainingDashboard] Pathway preview failed:', status, error?.message);
      }
    } finally {
      setLoadingPathway(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{
        background: 'radial-gradient(circle at 30% 10%, #FFF7FB, #EDE8FF, #FDF3E3)'
      }}>
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <motion.div 
            className="w-16 h-16 border-4 border-violet-300 border-t-violet-600 rounded-full mx-auto mb-4"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          <p className="text-violet-900 text-lg font-medium">Chargement de votre formation...</p>
        </motion.div>
      </div>
    );
  }

  const progressPercentage = Math.round(globalProgress);
  const progressMessage = 
    progressPercentage === 0 ? "🌟 Commencez votre parcours d'excellence" :
    progressPercentage < 25 ? "💪 Excellent début ! Continuez sur cette lancée" :
    progressPercentage < 50 ? "🎯 Vous êtes sur la bonne voie !" :
    progressPercentage < 75 ? "🚀 Plus que quelques étapes vers la maîtrise" :
    progressPercentage < 100 ? "⭐ Presque au sommet ! Vous y êtes presque" :
    "🏆 Félicitations ! Parcours terminé";

  return (
    <div className="min-h-screen" style={{
      background: 'radial-gradient(circle at 30% 10%, #FFF7FB, #EDE8FF, #FDF3E3)'
    }}>
      
      {/* Fixed Header */}
      <motion.header 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-violet-200/50"
        style={{ background: 'rgba(255, 255, 255, 0.85)' }}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center gap-4">
            {/* Logo cliquable — Iter305 : remplace le bouton "Accueil" supprimé */}
            <motion.button
              type="button"
              onClick={() => navigate('/')}
              data-testid="dashboard-logo-home"
              className="flex items-center gap-3 group"
              whileHover={{ scale: 1.02 }}
              aria-label="Retour à l'accueil"
            >
              <motion.div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: premiumTheme.gold.gradient }}
                animate={{ 
                  boxShadow: [
                    '0 0 20px rgba(251, 191, 36, 0.3)',
                    '0 0 30px rgba(251, 191, 36, 0.5)',
                    '0 0 20px rgba(251, 191, 36, 0.3)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Sparkles className="w-6 h-6 text-violet-900" />
              </motion.div>
              <div className="text-left">
                <h1 className="text-xl font-bold text-violet-900 group-hover:text-violet-700 transition-colors">
                  Coach DIA
                </h1>
                <p className="text-xs text-violet-600">Formation Premium</p>
              </div>
            </motion.button>

            {/* Iter305 — Top bar refondue : 3 zones, hiérarchie premium */}
            <div className="flex gap-2 flex-wrap items-center">
              {user && (
                <>
                  {/* Zone centrale — Actions principales (3 boutons symétriques) */}
                  <Button
                    onClick={() => navigate('/coach-vocal?mode=free')}
                    className="gap-2 text-white font-semibold"
                    style={{ background: premiumTheme.purple.gradient }}
                    size="sm"
                  >
                    <Mic className="w-4 h-4" />
                    <span className="hidden sm:inline">Coach Vocal IA</span>
                  </Button>

                  <Button
                    onClick={() => navigate('/marketplace')}
                    data-testid="marketplace-btn"
                    className="gap-2 text-white font-semibold"
                    style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F3D060 100%)' }}
                    size="sm"
                  >
                    <Users2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Coaching Humain</span>
                  </Button>

                  <Button
                    onClick={() => navigate('/user-dashboard')}
                    className="gap-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold hover:from-purple-700 hover:to-pink-600 shadow-lg hover:shadow-xl transition-all"
                    size="sm"
                  >
                    <TrendingUp className="w-4 h-4" />
                    <span className="hidden sm:inline">Mon parcours</span>
                  </Button>

                  {/* Bouton étoile — Test Prism / Pulse (renommé Iter305) */}
                  <Button
                    onClick={() => setShowInsightsModal(true)}
                    data-testid="training-insights-btn"
                    className="gap-2 text-white font-semibold"
                    style={{ background: 'linear-gradient(135deg, #92710A 0%, #D4AF37 60%, #F3D060 100%)' }}
                    size="sm"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span className="hidden sm:inline">Test Prism / Pulse</span>
                  </Button>

                  {/* Séparateur visuel entre actions et utilitaires */}
                  <span className="hidden lg:block w-px h-6 bg-violet-200 mx-1" aria-hidden />

                  {/* Zone droite — Utilitaires + CTA upsell */}
                  <Button
                    onClick={handlePremiumClick}
                    data-testid="premium-btn"
                    className="gap-2 text-violet-900 font-semibold"
                    style={{ background: premiumTheme.gold.gradient }}
                    size="sm"
                  >
                    <Crown className="w-4 h-4" />
                    <span className="hidden sm:inline">Premium</span>
                  </Button>

                  {user?.role === 'admin' && (
                    <Button
                      onClick={() => navigate('/admin')}
                      variant="outline"
                      className="gap-2 border-violet-300 text-violet-700 hover:bg-violet-50 flex"
                      size="sm"
                    >
                      <Settings className="w-4 h-4" />
                      <span className="hidden sm:inline">Admin</span>
                    </Button>
                  )}

                  {/* 🎯 Notifications — point d'entrée unique actions suggérées */}
                  <P12NotificationBadge token={token} />

                  {/* 🧭 Aide & Navigation — desktop (pill) + mobile (icon-only) */}
                  <button
                    type="button"
                    onClick={() => setHelpOpen(true)}
                    data-testid="training-help-btn-desktop"
                    aria-label="Ouvrir l'aide à la navigation"
                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-[#1C1147] transition-all hover:-translate-y-0.5 hover:shadow-md whitespace-nowrap"
                    style={{
                      background: 'rgba(255,252,236,0.9)',
                      border: '1.5px solid rgba(212,175,55,0.45)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85)',
                    }}
                  >
                    <HelpCircle className="w-4 h-4" style={{ color: '#B7901F' }} />
                    <span>Aide &amp; Navigation</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setHelpOpen(true)}
                    data-testid="training-help-btn-mobile"
                    aria-label="Ouvrir l'aide à la navigation"
                    className="sm:hidden inline-flex items-center justify-center w-11 h-11 rounded-full transition-all active:scale-95"
                    style={{
                      background: 'rgba(255,252,236,0.9)',
                      border: '1.5px solid rgba(212,175,55,0.45)',
                      boxShadow: '0 4px 12px -6px rgba(28,17,71,0.18), inset 0 1px 0 rgba(255,255,255,0.85)',
                    }}
                  >
                    <HelpCircle className="w-5 h-5" style={{ color: '#B7901F' }} />
                  </button>

                  {/* Iter305 — Déconnexion premium relief */}
                  <button
                    type="button"
                    onClick={handleLogout}
                    data-testid="dashboard-logout-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-[#1C1147] transition-all hover:-translate-y-0.5 active:scale-[0.98] hover:bg-[#FFF1ED] hover:border-[#B94A48] hover:text-[#B94A48]"
                    style={{
                      background: '#FFFFFF',
                      border: '1.5px solid rgba(212,175,55,0.45)',
                      boxShadow:
                        '0 6px 18px -8px rgba(28,17,71,0.18), inset 0 1px 0 rgba(255,255,255,0.85), inset 0 -1px 0 rgba(212,175,55,0.12)',
                    }}
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">Déconnexion</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content - Padding Responsive Mobile */}
      <main className="container mx-auto px-2 sm:px-4 lg:px-6 pt-28 pb-12">
        {/* Iter306 — Synthèse certifiante CoachDIA premium (6 blocs structurés) */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
          aria-labelledby="formation-synthese-heading"
          data-testid="formation-synthese"
        >
          <div
            className="rounded-3xl p-6 sm:p-8 lg:p-10 border"
            style={{
              background: '#FAFAF8',
              borderColor: 'rgba(212,175,55,0.25)',
              boxShadow: '0 12px 36px -16px rgba(28,17,71,0.10), inset 0 1px 0 rgba(255,255,255,0.6)',
            }}
          >
            {/* En-tête : Bloc 1 — Positionnement */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-8">
              <div className="flex-1">
                <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase font-semibold mb-3 text-[#B7901F]">
                  Votre Formation Certifiante
                </p>
                <h2
                  id="formation-synthese-heading"
                  className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1C1147] leading-[1.05] tracking-tight mb-3"
                >
                  Un système certifiant<br />
                  <span style={{
                    background: 'linear-gradient(90deg, #B7901F, #D4AF37, #F3D060)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}>
                    basé sur la pratique réelle.
                  </span>
                </h2>
                <p className="text-[#3A3252] text-base sm:text-lg leading-relaxed max-w-2xl">
                  Vous n'êtes pas évalué sur ce que vous comprenez.
                  Vous êtes évalué sur ce que vous démontrez en situation.
                </p>
              </div>

              {/* Anneau de progression compact */}
              <div className="relative shrink-0">
                <svg className="w-24 h-24 transform -rotate-90" aria-hidden>
                  <circle cx="48" cy="48" r="42" stroke="rgba(212,175,55,0.15)" strokeWidth="6" fill="none" />
                  <motion.circle
                    cx="48" cy="48" r="42"
                    stroke="url(#progressGradGold)"
                    strokeWidth="6" fill="none" strokeLinecap="round"
                    initial={{ strokeDashoffset: 264 }}
                    animate={{ strokeDashoffset: 264 - (264 * progressPercentage) / 100 }}
                    transition={{ duration: 1.4, ease: 'easeOut' }}
                    style={{ strokeDasharray: 264 }}
                  />
                  <defs>
                    <linearGradient id="progressGradGold" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#B7901F" />
                      <stop offset="50%" stopColor="#D4AF37" />
                      <stop offset="100%" stopColor="#F3D060" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-serif font-bold text-[#1C1147] tabular-nums">{progressPercentage}%</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#B7901F]">Complété</span>
                </div>
              </div>
            </div>

            {/* Bloc 2 — Fonctionnement en 5 étapes */}
            <div className="mb-7">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#B7901F] font-semibold mb-4">
                Le parcours en 5 étapes
              </p>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                {[
                  { n: 1, t: 'Vous comprenez',     icon: '📖' },
                  { n: 2, t: 'Vous vous entraînez', icon: '🎯' },
                  { n: 3, t: 'Vous êtes challengé', icon: '🔥' },
                  { n: 4, t: 'Vous êtes évalué',    icon: '🔍' },
                  { n: 5, t: 'Vous validez',        icon: '✓' },
                ].map((s, idx) => (
                  <div key={s.n} className="relative">
                    <div
                      className="rounded-2xl px-3 py-3.5 bg-white border border-[#EDE7D7] hover:border-[#D4AF37] transition-all flex items-start gap-2.5"
                      style={{ boxShadow: '0 2px 10px rgba(28,17,71,0.04)' }}
                    >
                      <span className="font-serif font-bold text-[#B7901F] text-sm tabular-nums shrink-0 mt-0.5">
                        0{s.n}
                      </span>
                      <span className="text-[13px] sm:text-sm font-semibold text-[#1C1147] leading-tight">{s.t}</span>
                    </div>
                    {idx < 4 && (
                      <span
                        className="hidden md:block absolute top-1/2 -right-1 w-2 h-px bg-[#D4AF37]/50"
                        aria-hidden
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Bloc 3 — Logique certifiante (méthode 2 × 5 = 10) */}
            <div
              className="rounded-2xl p-5 sm:p-6 mb-7"
              style={{
                background: 'linear-gradient(135deg, #FFF8E0 0%, #FFFCEC 100%)',
                border: '1.5px solid rgba(212,175,55,0.45)',
              }}
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#B7901F] font-semibold mb-4">
                La méthode certifiante
              </p>
              <div className="grid grid-cols-3 gap-4 sm:gap-6 text-center">
                <div>
                  <div className="font-serif text-3xl sm:text-4xl font-bold text-[#1C1147] tabular-nums mb-1">2</div>
                  <p className="text-xs sm:text-sm text-[#5A5266] leading-tight">objectifs<br />par module</p>
                </div>
                <div className="flex items-center justify-center">
                  <span className="font-serif text-2xl sm:text-3xl text-[#B7901F]" aria-hidden>×</span>
                </div>
                <div>
                  <div className="font-serif text-3xl sm:text-4xl font-bold text-[#1C1147] tabular-nums mb-1">5</div>
                  <p className="text-xs sm:text-sm text-[#5A5266] leading-tight">critères<br />par objectif</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#D4AF37]/30 text-center">
                <p className="text-sm sm:text-base text-[#3A3252]">
                  <span className="font-serif text-2xl sm:text-3xl font-bold text-[#B7901F] tabular-nums mr-1.5">10</span>
                  critères à <strong className="text-[#1C1147]">démontrer</strong> pour valider chaque module
                </p>
              </div>
            </div>

            {/* Blocs 4 + 5 — Owen + Certification */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-7">
              <div
                className="rounded-2xl p-5 bg-white border border-[#EDE7D7]"
                style={{ boxShadow: '0 4px 16px rgba(28,17,71,0.04)' }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: 'linear-gradient(135deg, #6D28D9, #A78BFA)' }}
                  >
                    <Brain className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#6D28D9] font-bold mb-1">
                      Le rôle d'Owen
                    </p>
                    <p className="text-sm text-[#3A3252] leading-relaxed">
                      <strong className="text-[#1C1147]">Analyse</strong> ·{' '}
                      <strong className="text-[#1C1147]">challenge</strong> ·{' '}
                      <strong className="text-[#1C1147]">valide uniquement sur preuves</strong>.
                    </p>
                  </div>
                </div>
              </div>
              <div
                className="rounded-2xl p-5 bg-white border border-[#D4AF37]/40"
                style={{ boxShadow: '0 4px 16px rgba(212,175,55,0.10)' }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: 'linear-gradient(135deg, #B7901F, #F3D060)' }}
                  >
                    <Award className="w-4 h-4 text-[#1C1147]" />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#B7901F] font-bold mb-1">
                      Certification
                    </p>
                    <p className="text-sm text-[#3A3252] leading-relaxed">
                      <strong className="text-[#1C1147]">Certificat</strong> à chaque module validé.
                      Preuves <strong className="text-[#1C1147]">tracées</strong> et opposables.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bloc 6 — Résultat final */}
            <div className="text-center pt-5 border-t border-[#EDE7D7]">
              <p className="font-serif text-lg sm:text-xl lg:text-2xl text-[#1C1147] leading-tight tracking-tight">
                Vos décisions deviennent{' '}
                <span style={{
                  background: 'linear-gradient(90deg, #B7901F, #D4AF37, #F3D060)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  des compétences mesurables et certifiées.
                </span>
              </p>
              <p className="text-xs sm:text-sm text-[#7A6F58] mt-2">{progressMessage}</p>
            </div>
          </div>
        </motion.section>

        {/* Iter307 — Timeline parcours + Cockpit Owen (layout 70/30) */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 lg:gap-8">
          {/* LEFT — Timeline parcours (70%) */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
            data-testid="parcours-timeline"
          >
            <div
              className="rounded-3xl p-5 sm:p-7 lg:p-9 border h-full"
              style={{
                background: '#FAFAF8',
                borderColor: '#E8E4D9',
                boxShadow: '0 12px 36px -16px rgba(28,17,71,0.08), inset 0 1px 0 rgba(255,255,255,0.6)',
              }}
            >
              {/* Header timeline */}
              <div className="flex items-start justify-between mb-7 gap-4">
                <div>
                  <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase font-semibold mb-2 text-[#B7901F]">
                    Votre Parcours
                  </p>
                  <h3 className="font-serif text-2xl sm:text-3xl text-[#1C1147] leading-tight tracking-tight">
                    Les 5 piliers du<br />leadership moderne.
                  </h3>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-serif text-2xl sm:text-3xl font-bold tabular-nums text-[#1C1147]">
                    {pillars.filter((p) => (pillarsProgress[p.id] || 0) >= 100).length}
                    <span className="text-[#B7901F]">/{pillars.length || 5}</span>
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-[#7A6F58]">piliers validés</p>
                </div>
              </div>

              {/* Timeline verticale — chaque pilier = node */}
              <ol className="relative space-y-2">
                {pillars.map((pillar, index) => {
                  const config = pillarConfig[index] || pillarConfig[0];
                  const PillarIcon = config.icon;
                  const progress = pillarsProgress[pillar.id] || 0;
                  const status = progress >= 100 ? 'done' : progress > 0 ? 'active' : 'todo';
                  const isLast = index === pillars.length - 1;

                  const dotColor = status === 'done'
                    ? '#48825A'
                    : status === 'active'
                    ? '#D4AF37'
                    : '#E8E4D9';

                  return (
                    <motion.li
                      key={pillar.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.08 * index, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      className="relative pl-12 sm:pl-14 pb-3 group"
                    >
                      {/* Connection line */}
                      {!isLast && (
                        <span
                          className="absolute left-[18px] sm:left-[22px] top-11 w-px"
                          style={{
                            height: 'calc(100% - 32px)',
                            background: status === 'done'
                              ? 'linear-gradient(180deg, #48825A 0%, rgba(232,228,217,0.6) 100%)'
                              : 'rgba(232,228,217,0.6)',
                          }}
                          aria-hidden
                        />
                      )}

                      {/* Node dot + index */}
                      <span
                        className="absolute left-0 top-1.5 w-9 sm:w-11 h-9 sm:h-11 rounded-full flex items-center justify-center font-serif font-bold text-sm tabular-nums transition-transform group-hover:scale-110"
                        style={{
                          background: status === 'done'
                            ? 'linear-gradient(135deg, #48825A, #6BAA7C)'
                            : status === 'active'
                            ? 'linear-gradient(135deg, #B7901F, #F3D060)'
                            : '#FFFFFF',
                          color: status === 'todo' ? '#7A6F58' : '#FFFFFF',
                          border: status === 'todo' ? '1.5px solid #E8E4D9' : 'none',
                          boxShadow: status !== 'todo' ? `0 6px 18px -6px ${dotColor}AA` : 'none',
                        }}
                      >
                        {status === 'done' ? <CheckCircle2 className="w-4 h-4" /> : `0${index + 1}`}
                      </span>

                      {/* Card */}
                      <button
                        type="button"
                        onClick={() => navigate(`/pillar/${pillar.id}`)}
                        data-testid={`parcours-node-${index + 1}`}
                        className="w-full text-left rounded-2xl p-4 sm:p-5 bg-white border border-[#EDE7D7] hover:border-[#D4AF37] hover:shadow-md transition-all group-hover:-translate-y-0.5"
                        style={{ boxShadow: '0 2px 10px rgba(28,17,71,0.04)' }}
                      >
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div
                            className="w-9 sm:w-10 h-9 sm:h-10 rounded-xl flex items-center justify-center shrink-0"
                            style={{
                              background: status === 'todo' ? '#FFF8E0' : 'rgba(212,175,55,0.12)',
                            }}
                          >
                            <PillarIcon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#B7901F' }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h4 className="text-sm sm:text-base font-semibold text-[#1C1147] leading-snug break-words">
                                {pillar.name || pillar.title}
                              </h4>
                              <span
                                className="shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                                style={{
                                  background: status === 'done' ? 'rgba(72,130,90,0.12)' : status === 'active' ? '#FFF8E0' : '#F5F2EA',
                                  color: status === 'done' ? '#48825A' : status === 'active' ? '#B7901F' : '#7A6F58',
                                }}
                              >
                                {status === 'done' ? 'Validé' : status === 'active' ? 'En cours' : 'À venir'}
                              </span>
                            </div>
                            <p className="text-xs sm:text-sm text-[#5A5266] line-clamp-2 sm:line-clamp-1 mb-2">
                              {pillar.description}
                            </p>
                            <div className="flex items-center gap-3">
                              <div className="flex-1 h-1.5 rounded-full bg-[#F5F2EA] overflow-hidden">
                                <motion.div
                                  className="h-full rounded-full"
                                  style={{
                                    background: status === 'done'
                                      ? 'linear-gradient(90deg, #48825A, #6BAA7C)'
                                      : 'linear-gradient(90deg, #B7901F, #D4AF37, #F3D060)',
                                  }}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${progress}%` }}
                                  transition={{ duration: 1, delay: 0.1 * index }}
                                />
                              </div>
                              <span className="text-xs font-bold tabular-nums text-[#1C1147]">
                                {Math.round(progress)}%
                              </span>
                              <ChevronRight className="w-4 h-4 text-[#B7901F] group-hover:translate-x-1 transition-transform shrink-0" />
                            </div>
                          </div>
                        </div>
                      </button>
                    </motion.li>
                  );
                })}
              </ol>
            </div>
          </motion.section>

          {/* RIGHT — Cockpit Owen (30%) */}
          <motion.aside
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-3"
            data-testid="owen-cockpit"
          >
            <div
              className="rounded-3xl p-5 sm:p-7 border h-full flex flex-col"
              style={{
                background: '#FFFFFF',
                borderColor: '#E8E4D9',
                boxShadow: '0 12px 36px -16px rgba(28,17,71,0.10)',
              }}
            >
              {/* Header cockpit */}
              <div className="mb-5">
                <div className="flex items-center gap-2.5 mb-2">
                  <span
                    className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #6D28D9, #A78BFA)',
                      boxShadow: '0 4px 14px -4px rgba(109,40,217,0.45)',
                    }}
                  >
                    <Brain className="w-4 h-4 text-white" />
                  </span>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#6D28D9] font-bold">
                    Owen · Centre de pilotage
                  </p>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl text-[#1C1147] leading-tight">
                  Votre système intelligent.
                </h3>
              </div>

              {/* État actuel */}
              <div
                className="rounded-2xl p-4 mb-4"
                style={{
                  background: 'linear-gradient(135deg, #FFF8E0 0%, #FFFCEC 100%)',
                  border: '1px solid rgba(212,175,55,0.3)',
                }}
                data-testid="owen-state"
              >
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#B7901F] font-semibold mb-2">
                  État actuel
                </p>
                <p className="font-serif text-2xl font-bold tabular-nums text-[#1C1147] leading-none mb-1">
                  {progressPercentage}<span className="text-base text-[#B7901F]">%</span>
                </p>
                <p className="text-xs text-[#5A5266]">
                  {progressPercentage === 0
                    ? 'Prêt à démarrer votre première étape.'
                    : progressPercentage < 30
                    ? 'Vous bâtissez les fondations.'
                    : progressPercentage < 70
                    ? 'Vous consolidez votre maîtrise.'
                    : progressPercentage < 100
                    ? 'Vous approchez de la certification complète.'
                    : 'Parcours fondamental certifié. Bravo.'}
                </p>
              </div>

              {/* Prochaine action recommandée */}
              {!loadingRecommendations && recommendations.length > 0 && (
                <div className="mb-4" data-testid="owen-next-action">
                  <p className="text-[10px] tracking-[0.28em] uppercase text-[#7A6F58] font-semibold mb-2">
                    Prochaine action recommandée
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      const rec = recommendations[0];
                      if (rec?.module_id) handleExploreModule(rec.module_id);
                      else toast.error('Module non disponible.');
                    }}
                    className="w-full text-left rounded-2xl p-4 bg-[#FAFAF8] border border-[#EDE7D7] hover:border-[#D4AF37] transition-all group/next"
                  >
                    <div className="flex items-center gap-3">
                      <Target className="w-4 h-4 text-[#B7901F] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1C1147] truncate">
                          {recommendations[0].module_title}
                        </p>
                        <p className="text-[11px] text-[#7A6F58] truncate">
                          {recommendations[0].reason}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#B7901F] group-hover/next:translate-x-1 transition-transform shrink-0" />
                    </div>
                  </button>
                </div>
              )}

              {/* Iter308 — Parcours personnalisé d'Owen (liste dynamique modules) */}
              {pathwayPreview?.modules?.length > 0 && (() => {
                const completedCount = pathwayPreview.modules.filter((m) => m.status === 'completed').length;
                const totalCount = pathwayPreview.modules.length;
                const nextModule = pathwayPreview.modules.find((m) => m.status === 'in_progress')
                  || pathwayPreview.modules.find((m) => m.status !== 'completed');
                const navigateToModule = (m) => {
                  if (m.source === 'dynamic') {
                    const dynamicId = (m.module_id || '').replace('dynamic:', '');
                    navigate(`/modules-dynamic/${dynamicId}`);
                  } else {
                    navigate(`/module-v2/${m.module_id}`);
                  }
                };
                return (
                  <>
                    {/* Prochaine meilleure action — en haut, dominante */}
                    {nextModule && (
                      <div
                        className="rounded-2xl p-4 mb-4 relative overflow-hidden"
                        style={{
                          background: 'linear-gradient(135deg, #FFF8E0 0%, #FFFCEC 50%, #FFF8E0 100%)',
                          border: '1.5px solid rgba(212,175,55,0.45)',
                          boxShadow: '0 0 0 4px rgba(212,175,55,0.06), 0 8px 24px -10px rgba(212,175,55,0.30)',
                        }}
                        data-testid="owen-next-best-action"
                      >
                        <div className="flex items-start gap-2.5 mb-2">
                          <Target className="w-4 h-4 text-[#B7901F] mt-0.5 shrink-0" />
                          <p className="text-[10px] tracking-[0.28em] uppercase text-[#B7901F] font-bold">
                            Prochaine meilleure action
                          </p>
                        </div>
                        <p className="text-sm text-[#1C1147] font-semibold leading-snug mb-3 line-clamp-2">
                          {nextModule.reason
                            ? nextModule.reason
                            : `Continuez sur « ${nextModule.module_title} » pour progresser.`}
                        </p>
                        <button
                          type="button"
                          onClick={() => navigateToModule(nextModule)}
                          data-testid="owen-next-action-cta"
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-[#1C1147] transition-all hover:-translate-y-0.5"
                          style={{
                            background: 'linear-gradient(135deg, #D4AF37, #F3D060)',
                            boxShadow: '0 6px 18px -6px rgba(212,175,55,0.55)',
                          }}
                        >
                          Commencer maintenant
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}

                    {/* Liste dynamique modules — Parcours recommandé par Owen */}
                    <div className="mb-5" data-testid="owen-pathway-list">
                      <div className="flex items-center justify-between mb-2.5">
                        <p className="text-[10px] tracking-[0.28em] uppercase text-[#7A6F58] font-semibold">
                          Parcours recommandé par Owen
                        </p>
                        <span className="text-[10px] font-bold tabular-nums text-[#1C1147]">
                          {completedCount}<span className="text-[#B7901F]">/{totalCount}</span>
                        </span>
                      </div>
                      <ol className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1 -mr-1">
                        {pathwayPreview.modules.slice(0, 7).map((m, idx) => {
                          const stStyle =
                            m.status === 'completed'
                              ? { dot: '#48825A', label: 'Validé', bg: 'rgba(72,130,90,0.10)', color: '#48825A' }
                              : m.status === 'in_progress'
                              ? { dot: '#D4AF37', label: 'En cours', bg: '#FFF8E0', color: '#B7901F' }
                              : { dot: '#E8E4D9', label: 'À faire', bg: '#F5F2EA', color: '#7A6F58' };
                          return (
                            <li
                              key={m.module_id || idx}
                              className="rounded-xl p-2.5 bg-[#FAFAF8] border border-[#EDE7D7] hover:border-[#D4AF37] transition-all group/path"
                              data-testid={`owen-pathway-item-${idx + 1}`}
                            >
                              <div className="flex items-center gap-2.5">
                                <span
                                  className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold tabular-nums"
                                  style={{
                                    background: m.status === 'completed' ? stStyle.dot : '#FFFFFF',
                                    color: m.status === 'completed' ? '#FFFFFF' : '#1C1147',
                                    border: m.status === 'completed' ? 'none' : `1.5px solid ${stStyle.dot}`,
                                  }}
                                  aria-hidden
                                >
                                  {m.status === 'completed' ? <CheckCircle2 className="w-3 h-3" /> : idx + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-[#1C1147] truncate">
                                    {m.module_title}
                                  </p>
                                  <span
                                    className="inline-block text-[9px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded-full mt-0.5"
                                    style={{ background: stStyle.bg, color: stStyle.color }}
                                  >
                                    {stStyle.label}
                                  </span>
                                </div>
                                {m.status !== 'completed' && (
                                  <button
                                    type="button"
                                    onClick={() => navigateToModule(m)}
                                    aria-label={`Commencer ${m.module_title}`}
                                    className="opacity-60 group-hover/path:opacity-100 transition-opacity shrink-0 w-7 h-7 rounded-full flex items-center justify-center bg-[#FFFCEC] hover:bg-[#FFF8E0]"
                                    data-testid={`owen-pathway-cta-${idx + 1}`}
                                  >
                                    <ChevronRight className="w-3.5 h-3.5 text-[#B7901F]" />
                                  </button>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                      {totalCount > 7 && (
                        <p className="text-[10px] text-[#7A6F58] text-center mt-2">
                          +{totalCount - 7} autres modules dans votre parcours
                        </p>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* CTA principal — Continuer mon évolution */}
              <button
                type="button"
                onClick={() => {
                  // Logique : continuer le 1er module en cours, sinon ouvrir intake si rien en cours
                  const next = pathwayPreview?.modules?.find((m) => m.status === 'in_progress')
                    || pathwayPreview?.modules?.find((m) => m.status !== 'completed');
                  if (next) {
                    if (next.source === 'dynamic') {
                      const dynamicId = (next.module_id || '').replace('dynamic:', '');
                      navigate(`/modules-dynamic/${dynamicId}`);
                    } else {
                      navigate(`/module-v2/${next.module_id}`);
                    }
                  } else {
                    setShowIntakeModal(true);
                  }
                }}
                data-testid="owen-cta-continue"
                className="mt-auto w-full rounded-full py-3.5 px-5 font-bold text-sm flex items-center justify-center gap-2 text-[#1C1147] hover:-translate-y-0.5 active:scale-[0.98] transition-all"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37, #F3D060)',
                  boxShadow: '0 10px 30px -10px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.45)',
                }}
              >
                <Sparkles className="w-4 h-4" />
                Continuer mon évolution
                <ChevronRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => setShowIntakeModal(true)}
                data-testid="owen-cta-customize"
                className="group mt-2.5 w-full rounded-full py-3 px-5 font-semibold text-sm text-[#1C1147] transition-all relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #FFFCEC 0%, #FFFFFF 50%, #F6E8FF 100%)',
                  border: '1.5px solid rgba(212,175,55,0.45)',
                  boxShadow:
                    '0 1px 0 rgba(255,255,255,0.7) inset, 0 8px 22px -10px rgba(122,79,207,0.25), 0 6px 18px -10px rgba(212,175,55,0.30)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = 'rgba(212,175,55,0.85)';
                  e.currentTarget.style.boxShadow =
                    '0 1px 0 rgba(255,255,255,0.8) inset, 0 14px 32px -12px rgba(122,79,207,0.40), 0 12px 28px -12px rgba(212,175,55,0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(212,175,55,0.45)';
                  e.currentTarget.style.boxShadow =
                    '0 1px 0 rgba(255,255,255,0.7) inset, 0 8px 22px -10px rgba(122,79,207,0.25), 0 6px 18px -10px rgba(212,175,55,0.30)';
                }}
              >
                {/* Halo or au hover, subtil */}
                <span
                  aria-hidden
                  className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background:
                      'radial-gradient(120% 80% at 50% 0%, rgba(212,175,55,0.18) 0%, transparent 60%)',
                  }}
                />
                <span className="relative inline-flex items-center justify-center gap-2">
                  <Sparkles className="w-4 h-4" style={{ color: '#B7901F' }} />
                  <span
                    style={{
                      backgroundImage:
                        'linear-gradient(135deg, #1C1147 0%, #5B3FA0 55%, #B7901F 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      letterSpacing: '0.01em',
                    }}
                  >
                    Personnaliser mon parcours
                  </span>
                  <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" style={{ color: '#7A4FCF' }} />
                </span>
              </button>
            </div>
          </motion.aside>
        </div>
      </main>

      {/* Intake Modal */}
      <IntakeModal 
        isOpen={showIntakeModal}
        onClose={() => setShowIntakeModal(false)}
        onComplete={handleIntakeComplete}
      />
      
      {/* 🎯 P1 MOA: Modale sélection type de compte (bouton PREMIUM) */}
      <AccountTypeSelectionModal
        isOpen={showTypeSelectionModal}
        onClose={() => setShowTypeSelectionModal(false)}
        onSelectIndividual={handleSelectIndividual}
        onSelectEnterprise={handleSelectEnterprise}
      />
      
      {/* Modale packs individuels (après sélection "Individuel") */}
      <IndividualSubscriptionModal
        isOpen={showPackModal}
        onClose={() => setShowPackModal(false)}
      />

      {/* ✨ COACHDIA COACHDIA Prism — modale d'orientation (2 choix, réutilise les flows existants) */}
      <AnimatePresence>
        {showInsightsModal && (
          <motion.div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            style={{ background: 'rgba(15,23,42,0.75)', backdropFilter: 'blur(8px)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowInsightsModal(false)}
            data-testid="insights-orientation-modal"
          >
            <motion.div
              className="relative w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl"
              style={{ background: 'linear-gradient(135deg, #FFFBEA 0%, #FFFFFF 100%)' }}
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowInsightsModal(false)}
                data-testid="insights-modal-close"
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-stone-100 transition"
                aria-label="Fermer"
              >
                <X className="w-5 h-5 text-stone-600" />
              </button>

              <div className="p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                       style={{ background: 'linear-gradient(135deg, #92710A, #F3D060)' }}>
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#92710A]">COACHDIA</p>
                    <h2 className="text-2xl font-bold text-[#1C1147]" style={{ fontFamily: 'Inter, sans-serif' }}>
                      COACHDIA Prism / Pulse
                    </h2>
                  </div>
                </div>
                <p className="text-sm text-[#4B5563] mb-8">
                  Découvrez votre profil comportemental premium — seul ou avec votre équipe.
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Option 1 — Prism (individuel) — Iter295 PREMIUM relief */}
                  <button
                    onClick={() => { setShowInsightsModal(false); navigate('/dashboard/prism'); }}
                    data-testid="insights-option-individual"
                    className="group relative p-6 text-left rounded-2xl border-2 border-[#D4AF37]/30 hover:border-[#D4AF37] bg-gradient-to-br from-white via-[#FBF8F1] to-[#F5EDDA] hover:shadow-[0_18px_44px_-10px_rgba(212,175,55,0.55)] hover:-translate-y-1 active:translate-y-0 transition-all duration-300 overflow-hidden"
                  >
                    {/* Halo doré au hover */}
                    <span className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-gradient-to-br from-[#F3D060]/40 to-transparent blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden />
                    {/* Badge */}
                    <div className="absolute top-3 right-3 bg-[#1F1A12] text-[#F3D060] text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-sm">
                      Individuel
                    </div>
                    <div className="relative">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#92710A] via-[#D4AF37] to-[#F3D060] flex items-center justify-center mb-4 shadow-md group-hover:scale-110 transition-transform">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-[#1C1147] mb-1">COACHDIA Prism</h3>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-[#92710A] font-semibold mb-3">
                        Profil personnel
                      </p>
                      <p className="text-sm text-[#4B5563] mb-5 leading-relaxed">
                        Cartographie comportementale premium. Vos leviers de leadership, votre signature unique.
                      </p>
                      <span className="inline-flex items-center gap-2 text-sm font-bold text-[#92710A] group-hover:gap-3 transition-all">
                        Lancer mon profil
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </button>

                  {/* Option 2 — Pulse (équipe) — Iter295 PREMIUM relief */}
                  <button
                    onClick={() => { setShowInsightsModal(false); navigate('/team-pulse/teams'); }}
                    data-testid="insights-option-team"
                    className="group relative p-6 text-left rounded-2xl border-2 border-[#1F1A12]/20 hover:border-[#1F1A12] bg-gradient-to-br from-[#1F1A12] via-[#2A2319] to-[#1F1A12] hover:shadow-[0_18px_44px_-10px_rgba(31,26,18,0.55)] hover:-translate-y-1 active:translate-y-0 transition-all duration-300 overflow-hidden"
                  >
                    {/* Halo doré au hover */}
                    <span className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-gradient-to-br from-[#F3D060]/30 to-transparent blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden />
                    {/* Badge */}
                    <div className="absolute top-3 right-3 bg-[#F3D060] text-[#1F1A12] text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-sm">
                      Équipe
                    </div>
                    <div className="relative">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F3D060] via-[#D4AF37] to-[#92710A] flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform">
                        <Users className="w-6 h-6 text-[#1F1A12]" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-1">COACHDIA Pulse</h3>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-[#F3D060] font-semibold mb-3">
                        Intelligence collective
                      </p>
                      <p className="text-sm text-white/70 mb-5 leading-relaxed">
                        Cartographie d'équipe, complémentarités, points de friction. Décisions collectives éclairées.
                      </p>
                      <span className="inline-flex items-center gap-2 text-sm font-bold text-[#F3D060] group-hover:gap-3 transition-all">
                        Analyser mon équipe
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🧭 Modales d'aide — desktop (≥lg) vs mobile (<lg) */}
      <div className="hidden lg:block">
        <HelpDesktopModal
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          userContext={{
            role: user?.role,
            isAffiliate: user?.role === 'affiliate',
            isB2BAdmin: false,
          }}
        />
      </div>
      <div className="lg:hidden">
        <HelpMobileSheet
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          userContext={{
            role: user?.role,
            isAffiliate: user?.role === 'affiliate',
            isB2BAdmin: false,
          }}
        />
      </div>
    </div>
  );
};

export default TrainingDashboard;
